import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  getDuplicateAthleteComparison,
  combineAthleteAccounts,
} from "../../../services/AdminService";
import { getApiErrorMessage } from "../../../components/AthleteProfileModal";
import DeleteConfirmationDialog from "../../../components/athlete-profile-modal/DeleteConfirmationDialog";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { cellColor } from "./duplicateMatching";

const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const IDENTITY_FIELDS = [
  { label: "Login Email", field: "username" },
  { label: "First Name", field: "firstName" },
  { label: "Last Name", field: "lastName" },
  { label: "High School", field: "highSchool" },
  { label: "Position", field: "primaryPosition" },
  { label: "Grad Year", field: "expectedYearOfGraduation" },
];

// Mirrors duplicateMatching's getMatchingFields: blank values are ignored, so
// a row only reads as a mismatch when two actual, differing values are present.
const fieldsMatch = (a, b, normalize) => {
  const values = [a, b].map(normalize).filter(Boolean);
  return values.every((v) => v === values[0]);
};

const fullName = (account) =>
  account ? `${account.firstName ?? ""} ${account.lastName ?? ""}`.trim() : "";

// combine.dateUTC comes back as a raw ISO date string (e.g. "2026-07-28");
// render it the way the rest of the admin UI shows dates rather than as-is.
const formatCombineDate = (dateUTC) => {
  if (!dateUTC) return "";
  const parsed = new Date(dateUTC);
  return Number.isNaN(parsed.getTime())
    ? dateUTC
    : parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
};

function DuplicateAthleteCompareDialog({ open, pair, onClose, onMerged }) {
  const apiInstance = useAxiosInstance();
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open || !pair) return;

    async function fetchComparison() {
      setLoading(true);
      setComparison(null);
      try {
        const data = await getDuplicateAthleteComparison(
          apiInstance,
          pair.primaryId,
          pair.secondaryId,
        );
        setComparison(data);
      } catch (error) {
        toast.error(
          getApiErrorMessage(error, "Failed to load account comparison."),
          { position: "top-right" },
        );
      } finally {
        setLoading(false);
      }
    }

    fetchComparison();
  }, [open, pair, apiInstance]);

  useEffect(() => {
    if (!open) {
      setComparison(null);
      setConfirmOpen(false);
    }
  }, [open]);

  if (!open || !pair) return null;

  const primary = comparison?.firstAccount;
  const secondary = comparison?.secondAccount;

  const handleClose = () => {
    if (merging) return;
    setConfirmOpen(false);
    onClose?.();
  };

  const handleMergeConfirm = async () => {
    if (merging) return;
    setMerging(true);
    try {
      await combineAthleteAccounts(
        apiInstance,
        pair.primaryId,
        pair.secondaryId,
      );
      const keptName = fullName(primary) || `#${pair.primaryId}`;
      const removedName = fullName(secondary) || `#${pair.secondaryId}`;
      toast.success(
        `Merged accounts: kept ${keptName}, removed ${removedName}.`,
        { position: "top-right" },
      );
      setConfirmOpen(false);
      onMerged?.(pair);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to merge accounts."), {
        position: "top-right",
      });
      setConfirmOpen(false);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-[95%] sm:w-[90%] md:w-full max-w-4xl rounded-md relative max-h-[90vh] flex flex-col">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-50"
        >
          <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-5 pb-2">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">
            Compare &amp; Merge Duplicate Accounts
          </h2>
          {!loading && comparison && (
            <p className="text-sm text-gray-600">
              Account <span className="font-semibold">#{pair.primaryId}</span>{" "}
              (higher ID) will be kept. Account{" "}
              <span className="font-semibold">#{pair.secondaryId}</span> will be
              permanently deleted when merged.
            </p>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <LoadingSpinner />
            </div>
          ) : !comparison ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              Unable to load account comparison.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-0 min-w-[600px] text-sm overflow-auto">
                <div />
                <AccountHeader account={primary} badge="keep" />
                <AccountHeader account={secondary} badge="remove" />

                {IDENTITY_FIELDS.map(({ label, field, normalize }) => {
                  const normalizeValue = normalize ?? normalizeText;
                  const isMatch = fieldsMatch(
                    primary?.[field],
                    secondary?.[field],
                    normalizeValue,
                  );
                  return (
                    <React.Fragment key={field}>
                      <div className="text-gray-700 py-2 border-b">{label}</div>
                      <div
                        className={`text-left border py-2 px-3 mr-2 ${cellColor(isMatch)}`}
                      >
                        {primary?.[field] || ""}
                      </div>
                      <div
                        className={`text-left border py-2 px-3 mr-2 ${cellColor(isMatch)}`}
                      >
                        {secondary?.[field] || ""}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              <div className="grid grid-cols-3 gap-0 min-w-[600px] text-sm mt-4">
                <div className="text-gray-700 font-semibold py-2 border-b">
                  Combines Attended
                </div>
                <CombinesList combines={primary?.combines} />
                <CombinesList combines={secondary?.combines} />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={merging}
            className="px-4 py-2 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={loading || !comparison || merging}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
          >
            Merge Accounts
          </button>
        </div>
      </div>

      <DeleteConfirmationDialog
        open={confirmOpen}
        onClose={() => {
          if (merging) return;
          setConfirmOpen(false);
        }}
        title="Merge duplicate accounts?"
        description={
          <>
            This action <span className="font-semibold">cannot be undone</span>.{" "}
            <span className="font-semibold">
              {fullName(secondary) || `Account #${pair.secondaryId}`}
            </span>{" "}
            (login email:{" "}
            <span className="font-semibold">{secondary?.username || "—"}</span>)
            will be permanently deleted — its account, athlete profile, and
            login access are removed and cannot be recovered.{" "}
            <span className="font-semibold">
              {fullName(primary) || `Account #${pair.primaryId}`}
            </span>{" "}
            (login email:{" "}
            <span className="font-semibold">{primary?.username || "—"}</span>)
            will be kept.
          </>
        }
        onConfirm={handleMergeConfirm}
        confirmLabel={merging ? "Merging…" : "Merge accounts"}
        confirmDisabled={merging}
      />
    </div>
  );
}

function AccountHeader({ account, badge }) {
  return (
    <div className="text-center pb-3">
      <div className="w-14 h-14 bg-gray-200 rounded-full mx-auto">
        <img
          alt={fullName(account) || "Athlete"}
          src={account?.profilePicture || "/default-profile-img.png"}
          className="object-cover h-full w-full rounded-full"
        />
      </div>
      <h3 className="font-semibold mt-2 text-blue-700 text-sm">
        {fullName(account) || "—"}
      </h3>
      <span
        className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
          badge === "keep"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700"
        }`}
      >
        {badge === "keep" ? "Keeps this account" : "Will be deleted"}
      </span>
    </div>
  );
}

function CombinesList({ combines }) {
  if (!combines || combines.length === 0) {
    return (
      <div className="px-3 py-2 text-xs text-gray-400 italic">
        No combines attended.
      </div>
    );
  }

  return (
    <ul className="px-3 py-2 space-y-2">
      {combines.map((combine, idx) => (
        <li
          key={combine.eventId ?? idx}
          className="text-xs text-gray-700 border-b border-gray-100 last:border-0 pb-2 last:pb-0"
        >
          <p className="font-medium text-gray-900">{combine.name}</p>
          <p className="text-gray-600">
            {[combine.city, combine.state].filter(Boolean).join(", ")}
          </p>
          <p className="text-gray-600">{formatCombineDate(combine.dateUTC)}</p>
        </li>
      ))}
    </ul>
  );
}

export default DuplicateAthleteCompareDialog;
